// 타임라인. **데이터를 받아 그리기만 한다** — 가져오기·읽음 올리기는 부르는 쪽 일이다.
//
// ## 스크롤은 두 가지만 안다
// 1. 맨 아래에 있었으면 새 줄이 와도 맨 아래에 머문다. 위를 읽고 있었으면 건드리지 않는다.
// 2. 과거를 앞에 붙일 때는 **보던 줄이 그 자리에 있게** 높이 차이만큼 되돌린다.
// 이 둘이 없으면 채팅은 읽을 수 없다 — 읽는 중에 화면이 튄다.

import type { Seq, UserId } from '@maru/protocol'
import { MessagesSquare } from 'lucide-react'
import { type ReactNode, useEffect, useLayoutEffect, useRef } from 'react'
import type { FileMap } from './attachment_view.tsx'
import type { EmojiLook, GroupLook } from './body_view.tsx'
import { fmt_day } from './format.ts'
import { useLocale, useT } from './i18n.ts'
import { EmptyState, Skeleton, Spinner } from './kit/feedback.tsx'
import type { CardMap } from './link_card.tsx'
import { type Line, MessageRow, type RowActions } from './message_row.tsx'
import type { PollLook } from './poll_card.tsx'
import type { Tally } from './reaction_bar.tsx'
import { UnreadLine } from './unread_line.tsx'

export interface MessageListProps extends RowActions {
  lines: readonly Line[]
  /** 이 seq **다음** 줄부터 안 읽음이다. */
  read_upto: Seq
  name_of(user: UserId): string | undefined
  /** 봇인가 — 줄 머리의 "봇" 표식. */
  bot_of?: ((user: UserId) => boolean) | undefined
  /** 그룹 번호 → 이름과 "내가 그 안에 있는가". 없으면 번호로 그린다. */
  group_of?: GroupLook | undefined
  /** 커스텀 이모지 이름 → 그림 주소. */
  emoji_of?: EmojiLook | undefined
  me: UserId
  /** 아직 첫 묶음을 기다리는 중. */
  loading?: boolean
  /** 더 오래된 줄이 없다. */
  reached_start?: boolean
  /** 위 끝에 닿았다. 과거 한 페이지를 부탁한다. */
  on_reach_top?(): void
  /** seq → 반응 집계. 없는 줄은 반응이 없는 것. */
  tallies?: ReadonlyMap<Seq, readonly Tally[]>
  /** seq → 투표. 없거나 `null` 이면 투표가 없는 줄. */
  polls?: ReadonlyMap<Seq, PollLook | null>
  /** seq → 답글 수. */
  reply_counts?: ReadonlyMap<Seq, number>
  /** seq → 마지막 답글 시각(ISO). 답글 단추 옆에 상대 시각으로 선다. */
  reply_last?: ReadonlyMap<Seq, string>
  files?: FileMap
  cards?: CardMap
  /** 검색에서 뛰어온 줄. 화면 가운데로 옮기고 도드라지게 그린다. */
  focus?: Seq | null
  /** 핀이 꽂힌 줄들. */
  pinned?: ReadonlySet<Seq>
  /** 내가 담아 둔 줄들. */
  saved?: ReadonlySet<Seq>
  /** 이 줄의 고치기 칸을 연다(↑). `n` 이 바뀔 때마다 한 번. */
  edit_ask?: { seq: Seq; n: number } | null
  /**
   * 채널의 처음에 둘 다음 할 일(새 채널의 [사람 추가], 1:1 방의 [프로필 보기]). 빈 채널이면 빈 상태 아래,
   * 글이 있으면 처음까지 올렸을 때 인트로 아래 — 글이 하나 생기자 사라지면 그 길을 다시 못 찾는다(3차 F282).
   */
  empty_action?: ReactNode
  /** 대화의 처음에 서는 인트로(3차 F32). 없으면 "대화의 처음이에요." */
  /** 대화의 처음. `face` 는 1:1 대화방의 상대 얼굴이다(3차 F259). */
  intro?: { title: string; detail?: string | undefined; face?: ReactNode } | undefined
  /** 내 키워드. 남의 글 본문에서 칠한다(3차 F169). */
  words?: readonly string[] | undefined
}

/** 반응 없는 줄에 줄 빈 목록. 렌더마다 `[]` 를 새로 만들면 `MessageRow` 의 memo 가 깨진다. */
const NO_TALLY: readonly Tally[] = []

/** 같은 사람이 이 시간 안에 이어 쓰면 한 덩어리로 본다. */
export const RUN_ON_MS = 5 * 60 * 1000

/** 이 안으로 오면 "맨 아래" 로 친다. 픽셀 하나 어긋났다고 고정이 풀리면 안 된다. */
const BOTTOM_SLACK = 40
/** 이 안으로 오면 "위 끝" 이다. 0 이면 미리 못 받아 빈 화면이 보인다. */
const TOP_SLACK = 120

/**
 * 줄마다 넘길 손들. 한데 모아 두면 `map` 안이 읽을 만해진다 —
 * `exactOptionalPropertyTypes` 라 "없으면 아예 안 넘긴다" 를 일일이 써야 한다.
 */
function hands_of(props: MessageListProps): RowActions {
  return {
    ...(props.on_edit === undefined ? {} : { on_edit: props.on_edit }),
    ...(props.on_delete === undefined ? {} : { on_delete: props.on_delete }),
    ...(props.on_hide_card === undefined ? {} : { on_hide_card: props.on_hide_card }),
    ...(props.on_react === undefined ? {} : { on_react: props.on_react }),
    ...(props.on_poll_vote === undefined ? {} : { on_poll_vote: props.on_poll_vote }),
    ...(props.on_poll_unvote === undefined ? {} : { on_poll_unvote: props.on_poll_unvote }),
    ...(props.on_poll_close === undefined ? {} : { on_poll_close: props.on_poll_close }),
    ...(props.on_thread === undefined ? {} : { on_thread: props.on_thread }),
    ...(props.on_who === undefined ? {} : { on_who: props.on_who }),
    ...(props.on_pin === undefined ? {} : { on_pin: props.on_pin }),
    ...(props.on_save === undefined ? {} : { on_save: props.on_save }),
    ...(props.on_remind === undefined ? {} : { on_remind: props.on_remind }),
    ...(props.on_unread === undefined ? {} : { on_unread: props.on_unread }),
    ...(props.on_link === undefined ? {} : { on_link: props.on_link }),
    ...(props.on_forward === undefined ? {} : { on_forward: props.on_forward }),
    ...(props.on_action === undefined ? {} : { on_action: props.on_action }),
    ...(props.shortcuts === undefined ? {} : { shortcuts: props.shortcuts }),
    ...(props.on_shortcut === undefined ? {} : { on_shortcut: props.on_shortcut }),
    ...(props.emoji_names === undefined ? {} : { emoji_names: props.emoji_names }),
  }
}

export function MessageList(props: MessageListProps) {
  const { lines, read_upto, name_of, me, loading, reached_start, on_reach_top, focus } = props
  const t = useT()
  const box = useRef<HTMLDivElement>(null)
  // 마지막으로 그렸을 때의 상태. 새로 그리기 전에 "아래였나" 를 알아야 한다.
  const was = useRef({
    at_bottom: true,
    /** 마지막 스크롤 이벤트의 `scrollTop` — 위로 올렸는지 가른다. */
    top: 0,
    height: 0,
    first: null as Seq | null,
    last: null as Seq | null,
  })

  const first = lines[0]?.seq ?? null
  const last = lines.at(-1)?.seq ?? null

  // biome-ignore lint/correctness/useExhaustiveDependencies: 줄이 새로 붙거나(`last`) 앞에 붙을 때(`first`)만 돈다 — `lines`·`me` 는 그때 읽을 뿐이다
  useLayoutEffect(() => {
    const el = box.current
    if (el === null) return
    const w = was.current
    // 내가 방금 쓴 줄. 위를 읽고 있었어도 보낸 것은 보여야 한다(슬랙과 같다).
    const mine_new = w.last !== null && last !== w.last && lines.at(-1)?.author === me
    if (w.first !== null && first !== null && first < w.first) {
      // 앞에 붙었다. 늘어난 높이만큼 내려 보던 줄을 제자리에 둔다.
      el.scrollTop += el.scrollHeight - w.height
    } else if (w.at_bottom || mine_new) {
      el.scrollTop = el.scrollHeight
      w.at_bottom = true
    }
    w.height = el.scrollHeight
    w.first = first
    w.last = last
    // `last` 가 바뀔 때(새 줄)와 `first` 가 바뀔 때(과거)만 돈다. 매 렌더마다 돌면
    // 수정 한 번에도 스크롤이 튄다.
  }, [first, last])

  // 줄은 `content-visibility: auto` 라 화면 밖에서는 자리표시 높이로 잰다. 새 줄을 바닥에 붙인 순간은 그
  // 높이라서, 실제로 그려지며(목록·링크 카드·그림) 커지면 모자란 만큼 바닥에서 뜬다(3차 J5 — 목록 두 줄이
  // 잘려 보였다). 바닥에 있던 동안은 줄이 커질 때도 따라 내려간다. 마지막 줄만 보면 안 된다 — 새로 고친 뒤
  // 반응 수·저장 표시는 바닥 몇 줄 **위**에 늦게 붙고 판도 늦게 줄어, 바닥이 그만큼(5~60px) 뜬 채 멈췄다(3차 F117).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `first`·`last` 는 읽지 않고 방아쇠로만 쓴다
  useLayoutEffect(() => {
    const el = box.current
    if (el == null || typeof ResizeObserver === 'undefined') return
    const watch = new ResizeObserver(() => {
      if (was.current.at_bottom) el.scrollTop = el.scrollHeight
    })
    // 판 자체도 본다 — 머리(북마크 줄)나 입력칸이 늦게 자라면 창이 줄어 바닥이 가려진다.
    watch.observe(el)
    for (const row of el.children) watch.observe(row)
    return () => watch.disconnect()
  }, [first, last])

  // 뛰어온 줄로 옮긴다. **한 번만** 옮긴다 — 매 렌더마다 돌면 읽는 중에 끌려간다. 다른 채널에서 뛰어오면
  // 그 줄이 아직 그려지지 않았거나 판이 새로 서므로(3차 F165 — 링크로 왔는데 맨 위에 멈췄다) 줄이 바뀔 때마다
  // 다시 찾되, 옮긴 뒤에는 같은 `focus` 로 다시 옮기지 않는다.
  const aimed = useRef<{ focus: Seq; box: HTMLDivElement } | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `first`·`last` 는 "줄이 섰다" 는 방아쇠로만 쓴다
  useLayoutEffect(() => {
    const list = box.current
    if (focus == null || list === null) return
    const a = aimed.current
    if (a !== null && a.focus === focus && a.box === list) return
    const el = list.querySelector(`[data-seq="${focus}"]`)
    if (el === null) return
    el.scrollIntoView({ block: 'center' })
    aimed.current = { focus, box: list }
    // 손으로 스크롤한 것과 같은 상태로 둔다 — 안 그러면 다음 줄이 와서 맨 아래로 튄다.
    was.current.at_bottom = false
  }, [focus, first, last])

  // 내 키워드를 칠한다. 줄이 바뀔 때마다 다시 칠한다 — 새 줄·고친 줄의 글자는 새 노드다. 칠하는 손은 키워드가
  // 있을 때만 따로 받는다 — 키워드를 안 쓰는 사람의 첫 화면에 실을 까닭이 없다.
  const words = props.words
  // biome-ignore lint/correctness/useExhaustiveDependencies: `lines` 는 "줄이 바뀌었다" 는 방아쇠로만 쓴다
  useEffect(() => {
    if (words === undefined || words.length === 0) return
    const authors = new Map(lines.map((l) => [String(l.seq), l.author]))
    const mine = (row: Element) => authors.get(row.getAttribute('data-seq') ?? '') === me
    let alive = true
    let undo = () => {}
    void import('./keyword_paint.ts').then((m) => {
      if (alive) undo = m.paint_keywords(box.current, words, mine)
    })
    return () => {
      alive = false
      undo()
    }
  }, [lines, words, me])

  function on_scroll() {
    const el = box.current
    if (el === null) return
    const w = was.current
    // 바닥에서 멀어졌다고 곧장 "위를 읽는 중" 이 아니다. 판 높이가 잠깐 바뀌어 난 스크롤 이벤트가 늦게 자란 줄과
    // 겹치면 멀어진 것처럼 읽힌다(3차 F117 — 새로 고친 뒤 바닥이 60px 뜬 채 멈췄다). 사람이 **위로** 올렸을 때만 끈다.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK) w.at_bottom = true
    else if (el.scrollTop < w.top) w.at_bottom = false
    w.top = el.scrollTop
    if (el.scrollTop < TOP_SLACK && !loading && !reached_start) on_reach_top?.()
  }

  if (loading && lines.length === 0) return <Waiting />
  if (lines.length === 0) {
    return (
      <div className="MessageList empty">
        <EmptyState
          icon={props.intro?.face ?? <MessagesSquare size={24} />}
          title={props.intro?.title ?? t('아직 메시지가 없어요')}
          description={props.intro?.detail ?? t('첫 메시지를 보내 보세요.')}
          action={props.empty_action}
        />
      </div>
    )
  }

  // 안 읽음 띠는 **첫 안 읽은 줄 위**에 한 번만 놓는다. 줄은 seq 순이라 첫 줄부터 끝까지가 안 읽음.
  // 내가 쓴 줄은 새 메시지가 아니다 — 띠를 연 뒤 한 줄 보내면 "새 메시지 6개" 로 늘던 것(3차 J4).
  // 지운 줄도 새 메시지가 아니다 — 읽을 것이 없는 묘비 위에 "새 메시지 1개" 띠가 섰다(3차 F267).
  const theirs = (l: (typeof lines)[number]) =>
    l.seq > read_upto && l.author !== me && l.deleted_at === null
  const at = lines.findIndex(theirs)
  const unread = at < 0 ? 0 : lines.slice(at).filter(theirs).length
  const first_unread = at < 0 ? null : (lines[at]?.seq ?? null)

  return (
    <div className="MessageList" ref={box} onScroll={on_scroll}>
      {reached_start &&
        (props.intro === undefined ? (
          <p className="start">{t('대화의 처음이에요.')}</p>
        ) : (
          <div className="start intro">
            {props.intro.face}
            <strong>{props.intro.title}</strong>
            {props.intro.detail !== undefined && <span>{props.intro.detail}</span>}
            {props.empty_action !== undefined && (
              <span className="intro-action">{props.empty_action}</span>
            )}
          </div>
        ))}
      {loading && (
        <p className="MessageList-more" role="status">
          <Spinner />
          {t('불러오는 중')}
        </p>
      )}
      {lines.map((line, i) => {
        const before = lines[i - 1]
        // 날이 바뀌는 줄 위에 구분선 — 시각만으로는 며칠에 걸친 채널에서 날이 안 갈린다(3차 F52).
        const new_day =
          before === undefined || day_of(before.written_at) !== day_of(line.written_at)
        const run_on =
          !new_day &&
          before !== undefined &&
          before.author === line.author &&
          // 채널에도 보낸 스레드 답글은 앞 줄의 이어 쓰기가 아니다 — 이름 없이 붙으면 앞 글의 연속으로 읽힌다.
          before.broadcast !== true &&
          line.broadcast !== true &&
          before.deleted_at === null &&
          line.deleted_at === null &&
          gap(before.written_at, line.written_at) < RUN_ON_MS
        return (
          <div key={line.seq} data-seq={line.seq}>
            {new_day && <DayLine at={line.written_at} />}
            {line.seq === first_unread && <UnreadLine count={unread} />}
            <MessageRow
              line={line}
              run_on={run_on}
              name_of={name_of}
              bot_of={props.bot_of}
              group_of={props.group_of}
              emoji_of={props.emoji_of}
              me={me}
              tallies={
                props.tallies?.get(line.seq) ??
                (props.on_react === undefined ? undefined : NO_TALLY)
              }
              poll={props.polls?.get(line.seq)}
              reply_count={props.reply_counts?.get(line.seq)}
              reply_last={props.reply_last?.get(line.seq)}
              focused={focus === line.seq}
              files={props.files}
              cards={props.cards}
              {...hands_of(props)}
              pinned={props.pinned?.has(line.seq) === true}
              saved={props.saved?.has(line.seq) === true}
              edit_ask={props.edit_ask?.seq === line.seq ? props.edit_ask.n : undefined}
            />
          </div>
        )
      })}
    </div>
  )
}

/** 첫 묶음을 기다리는 동안의 줄 모양. 회색 막대는 읽히지 않고 "불러오는 중" 한 번만 읽힌다. */
function Waiting() {
  const t = useT()
  const widths = ['3/4', '1/2', 'full', '1/3'] as const
  return (
    <div className="MessageList loading" role="status" aria-label={t('불러오는 중')} aria-busy>
      {widths.map((w) => (
        <div key={w} className="MessageRow skeleton">
          <span className="gutter">
            <Skeleton shape="circle" size="lg" />
          </span>
          <div className="main">
            <Skeleton width="1/3" />
            <Skeleton width={w} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** 기기 시간대의 날. 시각이 이상하면 빈 글자 — 모두 같은 날로 쳐서 구분선을 안 그린다. */
function day_of(at: string): string {
  const d = new Date(at)
  return Number.isNaN(d.getTime()) ? '' : d.toDateString()
}

/** 날 구분선. 가운데 알약에 날짜, 양쪽은 옅은 선. */
function DayLine({ at }: { at: string }) {
  // 날 이름도 시각과 같다 — 늦게 온 조직 언어를 따르게 언어를 받는다(3차 F265).
  const locale = useLocale()
  if (day_of(at) === '') return null
  return (
    <div className="DayLine">
      <time dateTime={at}>{fmt_day(at, locale)}</time>
    </div>
  )
}

function gap(older: string, newer: string): number {
  const a = new Date(older).getTime()
  const b = new Date(newer).getTime()
  // 시각이 이상하면 이어 쓴 것으로 보지 않는다 — 합쳐서 잘못 그리느니 나눠 그린다.
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.POSITIVE_INFINITY
  return b - a
}
